/**
 * Design tokens — Lectura márka.
 * Vizuális inspiráció: hasonló mély-lila brand + modern, kerekített kártya-rendszer
 * mint a piacon ismert magyar tanulási marketplace-eken.
 *
 * A skálát Tailwind v4 `@theme`-be exportáljuk a `globals.css`-ben.
 */

export const colors = {
  // Brand — mély lila → magenta gradiens
  brand: {
    50: '#f5f1ff',
    100: '#ebe3ff',
    200: '#d6c7ff',
    300: '#b69cff',
    400: '#9166ff',
    500: '#7339f0',
    600: '#5d24d6',
    700: '#4b1cad',
    800: '#3c1786',
    900: '#2a0f5e',
    950: '#1a073d',
  },
  // Accent — meleg sárga/borostyán a CTA-knak
  accent: {
    50: '#fff8e6',
    100: '#ffefc2',
    200: '#ffdd85',
    300: '#ffc847',
    400: '#ffb01a',
    500: '#f59800',
    600: '#cc7900',
    700: '#a45a00',
    800: '#7a4200',
    900: '#5c3000',
  },
  // Sikeres / hibás állapotok
  success: {
    500: '#10b981',
    600: '#059669',
  },
  danger: {
    500: '#ef4444',
    600: '#dc2626',
  },
  warning: {
    500: '#f59e0b',
    600: '#d97706',
  },
  info: {
    500: '#3b82f6',
    600: '#2563eb',
  },
  // Semleges szürke skála (cool tint a brand felé)
  neutral: {
    0: '#ffffff',
    50: '#fafafb',
    100: '#f4f4f6',
    200: '#e6e6eb',
    300: '#d1d1da',
    400: '#9b9bab',
    500: '#6e6e80',
    600: '#4d4d5d',
    700: '#363645',
    800: '#22222e',
    900: '#15151e',
    950: '#0a0a10',
  },
} as const;

export const radius = {
  none: '0',
  sm: '0.375rem',
  md: '0.625rem',
  lg: '0.875rem',
  xl: '1.125rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  full: '9999px',
} as const;

export const shadow = {
  card: '0 1px 2px 0 rgba(15, 7, 50, 0.04), 0 4px 16px -2px rgba(15, 7, 50, 0.06)',
  'card-hover': '0 8px 24px -4px rgba(91, 33, 200, 0.18), 0 2px 6px -2px rgba(15, 7, 50, 0.08)',
  popover: '0 12px 32px -8px rgba(15, 7, 50, 0.18)',
  inner: 'inset 0 1px 2px 0 rgba(15, 7, 50, 0.06)',
} as const;

export const fontFamily = {
  sans: ['"Inter Variable"', '"Inter"', 'system-ui', '-apple-system', 'sans-serif'],
  display: ['"Plus Jakarta Sans Variable"', '"Plus Jakarta Sans"', '"Inter Variable"', 'sans-serif'],
  serif: ['"Source Serif 4"', '"Source Serif Pro"', 'Georgia', 'serif'],
  mono: ['"JetBrains Mono Variable"', '"JetBrains Mono"', 'ui-monospace', 'monospace'],
} as const;

export const fontSize = {
  xs: ['0.75rem', { lineHeight: '1rem' }],
  sm: ['0.875rem', { lineHeight: '1.25rem' }],
  base: ['1rem', { lineHeight: '1.5rem' }],
  lg: ['1.125rem', { lineHeight: '1.75rem' }],
  xl: ['1.25rem', { lineHeight: '1.875rem' }],
  '2xl': ['1.5rem', { lineHeight: '2rem' }],
  '3xl': ['1.875rem', { lineHeight: '2.375rem' }],
  '4xl': ['2.25rem', { lineHeight: '2.75rem' }],
  '5xl': ['3rem', { lineHeight: '1.1' }],
  '6xl': ['3.75rem', { lineHeight: '1.05' }],
} as const;

export const spacing = {
  // 4px alapú skála + brand-specifikus értékek
  px: '1px',
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
} as const;

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

export const motion = {
  duration: {
    fast: '120ms',
    base: '180ms',
    slow: '280ms',
    'extra-slow': '480ms',
  },
  easing: {
    DEFAULT: 'cubic-bezier(0.22, 1, 0.36, 1)',
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',
    in: 'cubic-bezier(0.7, 0, 0.84, 0)',
    'in-out': 'cubic-bezier(0.65, 0, 0.35, 1)',
  },
} as const;

export const tokens = {
  colors,
  radius,
  shadow,
  fontFamily,
  fontSize,
  spacing,
  breakpoints,
  motion,
} as const;

export type DesignTokens = typeof tokens;
